import aiGenerateApi from './aiGenerate'

const META_SYSTEM_PROMPT = `
You are an expert SEO copywriter specializing in travel, camping, and destination marketing for Lake Garda (Lago di Garda).

CRITICAL: Respond ONLY with a valid JSON object. No markdown, no code fences, no explanations.

Context:
- Lago di Garda Camping is a booking platform aggregating 18+ campsites and tourist villages around Lake Garda
- Target audience: European families, outdoor enthusiasts, camping travelers seeking nature experiences
- Key differentiators: 28,000+ beds, direct booking, diverse accommodation types (pitches, mobile homes, glamping, bungalows)
- Core topics: camping vacations, Lake Garda tourism, outdoor activities, family-friendly and eco-friendly accommodations

JSON structure required:
{
  "metaTitle": "string",
  "metaDescription": "string"
}

Requirements for metaTitle:
- Maximum 55-60 characters (including spaces)
- Extract and use the PRIMARY keyword/topic from the page content provided below
- Include location "Lago di Garda" or "Lake Garda" when relevant to page topic
- Front-load the most important keyword
- Use natural, compelling language (avoid keyword stuffing)
- Match search intent based on page type: transactional (booking/offers) vs informational (guides/articles)
- Use proper capitalization (title case or sentence case, no ALL CAPS)

Requirements for metaDescription:
- Maximum 150-160 characters (including spaces)
- Summarize the SPECIFIC value proposition of the content provided below
- Extract 1-2 most relevant keywords from the text
- Highlight unique aspects (specific campsite name, activity, location, or offer details)
- Use active voice and actionable language (Call to Action)
- Reference specific features mentioned in the text
- End with proper punctuation
`

function buildMetaPrompt(resourceData, language) {
  const langLabel = {
    italiano: 'Italian',
    inglese: 'English',
    tedesco: 'German',
    olandese: 'Dutch',
    danese: 'Danish',
    polacco: 'Polish',
  }[language]

  return `Instructions:
1. Analyze the "Resource data" below to identify the page topic and keywords
2. Determine if the content is transactional (booking) or informational
3. Generate a title under 60 chars that captures the main topic
4. Generate a description under 160 chars that summarizes the specific content
5. Ensure the output language matches ${langLabel}

Resource data (page content):
${JSON.stringify(resourceData, null, 2)}

Target language: ${langLabel}
`
}

/**
 * Generate meta title and description using the generic text-generation API.
 * Prompt is built in the admin based on resourceData and language.
 * @param {object} resourceData
 * @param {string} language
 * @param {'gpt-4o-mini' | 'gpt-5-nano'} [model] - Optional. If omitted, server uses gpt-4o-mini.
 */
const generateMeta = async (resourceData, language, model) => {
  const prompt = buildMetaPrompt(resourceData, language)
  const response = await aiGenerateApi.generateStructuredText({
    prompt,
    systemPrompt: META_SYSTEM_PROMPT,
    model: model || 'gpt-4o-mini',
  })

  if (!response.ok || !response.data) return response

  const parsed = response.data
  if (typeof parsed.metaTitle !== 'string' || typeof parsed.metaDescription !== 'string') {
    return { ok: false, data: { error: 'Invalid response from generation service.' } }
  }
  return {
    ok: true,
    data: {
      metaTitle: String(parsed.metaTitle).trim(),
      metaDescription: String(parsed.metaDescription).trim(),
    },
  }
}

export default { generateMeta }
